import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, Pill } from "lucide-react";

function InfoBlock({ label, value }) {
  if (!value?.trim()) return null;
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1">{label}</p>
      <p className="text-sm text-slate-700 whitespace-pre-wrap">{value}</p>
    </div>
  );
}

export function formatProductTitle(product) {
  if (!product) return "";
  if (product.brand) return `${product.name} — ${product.brand}`;
  return product.name;
}

export default function SupplementProductDetail({ product, compact = false, className = "" }) {
  if (!product) return null;

  const title = formatProductTitle(product);

  if (compact) {
    return (
      <div className={`flex items-start gap-3 min-w-0 ${className}`}>
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={title}
            className="w-14 h-14 rounded-lg object-cover shrink-0 border border-slate-100"
          />
        ) : (
          <div className="w-14 h-14 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
            <Pill className="w-6 h-6 text-indigo-600" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900 truncate">{product.name}</p>
          {product.brand && <p className="text-xs text-slate-500">{product.brand}</p>}
          <div className="flex flex-wrap gap-1 mt-1">
            {product.category && <Badge variant="secondary" className="text-[10px]">{product.category}</Badge>}
            {product.serving_size && <Badge variant="outline" className="text-[10px]">{product.serving_size}</Badge>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex flex-col sm:flex-row gap-4">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={title}
            className="w-full sm:w-40 h-40 rounded-xl object-cover border border-slate-100 shrink-0"
          />
        ) : (
          <div className="w-full sm:w-40 h-40 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0">
            <Pill className="w-12 h-12 text-indigo-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-lg font-bold text-slate-900">{product.name}</h3>
          {product.brand && <p className="text-sm text-indigo-600 font-medium mt-0.5">{product.brand}</p>}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {product.category && <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100">{product.category}</Badge>}
            {product.flavor && <Badge variant="outline">{product.flavor}</Badge>}
            {product.price_hint && <Badge variant="secondary">{product.price_hint}</Badge>}
          </div>
          {(product.serving_size || product.servings_per_container) && (
            <p className="text-xs text-slate-500 mt-2">
              {product.serving_size && <>Portion : {product.serving_size}</>}
              {product.serving_size && product.servings_per_container && " · "}
              {product.servings_per_container && <>{product.servings_per_container} portions</>}
            </p>
          )}
          {product.product_url && (
            <Button asChild size="sm" className="mt-3 rounded-full bg-indigo-600 hover:bg-indigo-700">
              <a href={product.product_url} target="_blank" rel="noreferrer">
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                Voir le produit
              </a>
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-3">
        <InfoBlock label="Description" value={product.description} />
        <InfoBlock label="Bénéfices" value={product.benefits} />
        <InfoBlock label="Mode d'emploi" value={product.usage_instructions} />
        <InfoBlock label="Composition" value={product.ingredients} />
        <InfoBlock label="Valeurs nutritionnelles" value={product.nutrition_facts} />
        <InfoBlock label="Allergènes" value={product.allergens} />
      </div>
    </div>
  );
}